import { FC } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { Tables } from '$/database.types';
import {
  GOVERNANCE_CHAIN_STEP_COLORS,
  GOVERNANCE_CHAIN_STEP_I18N_KEY,
} from '@/constants/governanceChainSteps.ts';
import { cn } from '@/utils/classNames.ts';

type LedgerEntry = Tables<'ledger_entries'>;

type Props = {
  entries: LedgerEntry[];
  className?: string;
};

function truncateHash(hash: string): string {
  if (hash.length <= 16) return hash;
  return `${hash.slice(0, 8)}…${hash.slice(-6)}`;
}

function stepLabel(
  step: string,
  t: (key: string, options?: { defaultValue?: string }) => string,
): string {
  return t(GOVERNANCE_CHAIN_STEP_I18N_KEY(step), { defaultValue: step.replace(/_/g, ' ') });
}

const GovernanceChainList: FC<Props> = ({ entries, className }) => {
  const { t } = useTranslation();

  if (entries.length === 0) {
    return (
      <p className={cn('font-dm text-sm text-[rgba(0,0,0,0.5)]', className)}>
        {t('governanceChain.empty')}
      </p>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {entries.map((entry, i) => {
        const payload = entry.payload as Record<string, unknown> | null;
        const isAdminOverride =
          entry.step === 'escalation' && payload?.event === 'admin_override';
        const badgeClass =
          GOVERNANCE_CHAIN_STEP_COLORS[entry.step] ?? 'bg-[#f4f4f4] text-[rgba(0,0,0,0.5)]';
        const label = isAdminOverride
          ? t('governanceChain.adminOverride')
          : stepLabel(entry.step, t);

        return (
          <div
            key={entry.id}
            className={cn(
              'flex flex-col gap-2 p-3 rounded-xl border border-[rgba(204,204,204,0.4)]',
              i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]',
            )}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    'font-dm text-xs font-bold px-2 py-0.5 rounded-full',
                    badgeClass,
                  )}
                >
                  {label}
                </span>
                {entry.actor_role && (
                  <span className="font-dm text-xs text-[rgba(0,0,0,0.55)]">
                    {t('governanceChain.decisionActor')}: {entry.actor_role}
                  </span>
                )}
                {entry.framework_attribution && (
                  <span className="font-dm text-xs text-[rgba(0,0,0,0.55)]">
                    {entry.framework_attribution}
                  </span>
                )}
                {entry.confidence_score != null && (
                  <span className="font-dm text-xs text-[rgba(0,0,0,0.55)]">
                    {t('governanceChain.confidenceRecord')}: {entry.confidence_score}
                  </span>
                )}
              </div>
              <span className="font-dm text-xs text-[rgba(0,0,0,0.4)] shrink-0">
                {dayjs(entry.created_at).format('MMM D, YYYY · HH:mm')}
              </span>
            </div>
            {entry.hash && (
              <p className="font-mono text-[11px] text-[rgba(0,0,0,0.45)] break-all">
                {t('governanceChain.hashChainIntegrity')}:{' '}
                <span title={entry.hash}>{truncateHash(entry.hash)}</span>
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GovernanceChainList;
